import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { seoHead } from "@/lib/seoHead";
import { AdminRoute } from "@/components/auth/AdminRoute";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/saude")({
  head: () => seoHead({ title: "Saúde do site | Admin", description: "Vigia automático do site.", noindex: true }),
  component: () => (
    <AdminRoute>
      <AdminLayout title="Saúde do site">
        <SaudePage />
      </AdminLayout>
    </AdminRoute>
  ),
});

function SaudePage() {
  const qc = useQueryClient();
  const last = useQuery({
    queryKey: ["health-last"],
    queryFn: async () => {
      const { data } = await supabase.from("_app_config").select("value").eq("key", "site_health_last").maybeSingle();
      try { return data?.value ? JSON.parse(data.value) as { at: string; ok: boolean; detail: string } : null; } catch { return null; }
    },
  });
  const events = useQuery({
    queryKey: ["health-events"],
    queryFn: async () => {
      const { data } = await (supabase as any).from("site_health_events").select("*").order("created_at", { ascending: false }).limit(30);
      return (data ?? []) as any[];
    },
  });
  const paused = useQuery({
    queryKey: ["health-paused"],
    queryFn: async () => {
      const { data } = await (supabase as any).rpc("health_paused_jobs");
      return (data ?? []) as { jobname: string; schedule: string }[];
    },
  });

  const resume = async (job: string) => {
    const { error } = await (supabase as any).rpc("health_resume_job", { _job: job });
    if (error) { toast.error(error.message); return; }
    toast.success(`Rotina "${job}" religada`);
    qc.invalidateQueries({ queryKey: ["health-paused"] });
    qc.invalidateQueries({ queryKey: ["health-events"] });
  };

  const fmt = (d: string) => new Date(d).toLocaleString("pt-BR");

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">Saúde do site</h1>
      <Card>
        <CardHeader><CardTitle>Última conferência</CardTitle></CardHeader>
        <CardContent>
          {last.data ? (
            <div className="space-y-1">
              <p className={last.data.ok ? "text-brand-green font-semibold" : "text-destructive font-semibold"}>
                {last.data.ok ? "No ar" : "Fora do ar"} — {fmt(last.data.at)}
              </p>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">{last.data.detail}</p>
            </div>
          ) : <p className="text-muted-foreground">Nenhuma conferência ainda.</p>}
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle>Rotinas pesadas pausadas</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {(paused.data ?? []).length === 0 && <p className="text-muted-foreground">Nenhuma.</p>}
          {(paused.data ?? []).map((j) => (
            <div key={j.jobname} className="flex items-center justify-between gap-3 border-b border-border pb-2">
              <span className="font-medium">{j.jobname} <span className="text-xs text-muted-foreground">({j.schedule})</span></span>
              <Button size="sm" onClick={() => resume(j.jobname)}>Religar</Button>
            </div>
          ))}
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle>Histórico de quedas</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {(events.data ?? []).length === 0 && <p className="text-muted-foreground">Nenhuma queda registrada.</p>}
          {(events.data ?? []).map((e) => (
            <details key={e.id} className="rounded-md border border-border p-3">
              <summary className="cursor-pointer">
                <b>{fmt(e.created_at)}</b> — causa provável: {e.culprit_job ?? "não identificada"}
                {e.resolved_at && <span className="ml-2 text-xs text-muted-foreground">(religada {fmt(e.resolved_at)})</span>}
              </summary>
              <pre className="mt-2 max-h-80 overflow-auto text-xs whitespace-pre-wrap">{e.details}{"\n\n"}{JSON.stringify({ acoes: e.actions, retrato: e.snapshot }, null, 2)}</pre>
            </details>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
